import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { PermissionGuard } from '../components/guards/PermissionGuard';
import { AppLayout } from '../components/layout/AppLayout';
import { useTenant } from '../contexts/TenantContext';
import { CMSLayout, cmsRoutes } from '@riptik/cms-backoffice-module';
import { RiptikCMSProvider } from '../providers/RiptikCMSProvider';
import { FnbPosLayout, OutletsPage, OrdersPage, OrderDetailPage, MasterDataPage, WarehousesPage, MenuPage, MaterialsPage, StockOpsPage, PurchasingPage, ReportsPage, TerminalShiftPage, ReservationWaitlistPage, KitchenConfigPage, FloorPlanPage, SelfOrderQRPage, TakeawayPage, ReservationRequestsPage, TenantConfigPage, StaffPage, BillDesignerPage, ReportDesignerPage, PaymentHostCredentialPage, PaymentReconciliationPage } from '@riptik/fnbpos-backoffice-module';
import { FnbPosProvider } from '../providers/FnbPosProvider';
import { CourtLayout, VenuesPage, CourtsPage, CourtPricingPage, BookingsPage, VenueSettingsPage, CourtStaffPage, ClosuresPage, DepositsPage, RefundsPage, ReportsPage as CourtReportsPage, EventRentalsPage } from '@riptik/court-backoffice-module';
import { CourtProvider } from '../providers/CourtProvider';
import { TournamentProvider } from '../providers/TournamentProvider';
import { TournamentLayout, TournamentsPage, TournamentDetailPage, TournamentRefundsPage } from '@riptik/tournament-backoffice-module';

// Lazy load page components for code splitting
const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const DashboardPage = lazy(() => import('../pages/dashboard/DashboardPage'));

// Platform Admin Pages
const SystemStatusPage = lazy(() => import('../pages/admin/SystemStatusPage'));
const UserManagementPage = lazy(() => import('../pages/admin/UserManagementPage'));
const OrganizationManagementPage = lazy(() => import('../pages/admin/OrganizationManagementPage'));
const AuditLogsPage = lazy(() => import('../pages/admin/AuditLogsPage'));
const ImportOrganizationPage = lazy(() => import('../pages/import/ImportOrganizationPage'));

// Organization Pages
const OrganizationDashboardPage = lazy(() => import('../pages/organization/OrganizationDashboardPage'));
const OrganizationUsersPage = lazy(() => import('../pages/OrganizationUsersPage'));
const OrganizationSettingsPage = lazy(() => import('../pages/organization/OrganizationSettingsPage'));
const SeatingPlansPage = lazy(() => import('../pages/seating/SeatingPlansPage'));

// Event Management Pages
const EventListPage = lazy(() => import('../pages/events/EventListPage'));
const EventFormPage = lazy(() => import('../pages/events/EventFormPage'));
const ImportEventPage = lazy(() => import('../pages/import/ImportEventPage'));
const EventDetailsPage = lazy(() => import('../pages/events/EventDetailsPage'));
const EventDashboardPage = lazy(() => import('../pages/events/EventDashboardPage'));

// Order Management Pages
const OrderListPage = lazy(() => import('../pages/orders/OrderListPage'));
const OrderDetailsPage = lazy(() => import('../pages/orders/OrderDetailsPage'));
const OrderGroupListPage = lazy(() => import('../pages/order-groups/OrderGroupListPage'));
const OrderGroupDetailsPage = lazy(() => import('../pages/order-groups/OrderGroupDetailsPage'));
const CheckinDeviceListPage = lazy(() => import('../pages/checkindevices/CheckinDeviceListPage'));

// Items/Products Management Pages
const ItemListPage = lazy(() => import('../pages/items/ItemListPage'));
const ItemFormPage = lazy(() => import('../pages/items/ItemFormPage'));

// Quota Management Pages
const QuotaListPage = lazy(() => import('../pages/quotas/QuotaListPage'));
const QuotaFormPage = lazy(() => import('../pages/quotas/QuotaFormPage'));
const EventGroupsPage = lazy(() => import('../pages/events/EventGroupsPage'));
const SubEventListPage = lazy(() => import('../pages/subevents/SubEventListPage'));
const SubEventFormPage = lazy(() => import('../pages/subevents/SubEventFormPage'));
const SubEventPriceGridPage = lazy(() => import('../pages/subevents/SubEventPriceGridPage'));

// Category Management Pages
const CategoryListPage = lazy(() => import('../pages/categories/CategoryListPage'));
// G9-19 (mantik-cinema-chain.md §5B) — tag scope theo ORGANIZER (không theo event như Category).
const TagListPage = lazy(() => import('../pages/tags/TagListPage'));
const TagFormPage = lazy(() => import('../pages/tags/TagFormPage'));
const UnitPairConfigPage = lazy(() => import('../pages/unitledger/UnitPairConfigPage'));
const SettlementRunListPage = lazy(() => import('../pages/unitledger/SettlementRunListPage'));
const SettlementRunDetailPage = lazy(() => import('../pages/unitledger/SettlementRunDetailPage'));
const ManualLedgerEntryPage = lazy(() => import('../pages/unitledger/ManualLedgerEntryPage'));
const OrphanLedgerEntriesPage = lazy(() => import('../pages/unitledger/OrphanLedgerEntriesPage'));
const OwnedEventConfigPage = lazy(() => import('../pages/unitledger/OwnedEventConfigPage'));
const InternalBalancesPage = lazy(() => import('../pages/unitledger/InternalBalancesPage'));
const CategoryFormPage = lazy(() => import('../pages/categories/CategoryFormPage'));

// Voucher Management Pages
const VoucherListPage = lazy(() => import('../pages/vouchers/VoucherListPage'));
const VoucherCreatePage = lazy(() => import('../pages/vouchers/VoucherCreatePage'));
const VoucherEditPage = lazy(() => import('../pages/vouchers/VoucherEditPage'));
const VoucherUsageReportPage = lazy(() => import('../pages/vouchers/VoucherUsageReportPage'));

// Promotion Management Pages (GĐ1 — ticket-only, org-level, mirror voucher org-level pattern)
const PromotionListPage = lazy(() => import('../pages/promotions/PromotionListPage'));
const PromotionCreatePage = lazy(() => import('../pages/promotions/PromotionCreatePage'));
const PromotionEditPage = lazy(() => import('../pages/promotions/PromotionEditPage'));

// Payment Management Pages
const PaymentListPage = lazy(() => import('../pages/payments/PaymentListPage'));
const PaymentDetailsPage = lazy(() => import('../pages/payments/PaymentDetailsPage'));

// Gift Card Pages
const GiftCardDashboardPage = lazy(() => import('../pages/giftcards/GiftCardDashboardPage'));
const GiftCardListPage = lazy(() => import('../pages/giftcards/GiftCardListPage'));
const GiftCardCreatePage = lazy(() => import('../pages/giftcards/GiftCardCreatePage'));
const GiftCardDetailsPage = lazy(() => import('../pages/giftcards/GiftCardDetailsPage'));
const GiftCardRefundTasksPage = lazy(() => import('../pages/giftcards/GiftCardRefundTasksPage'));

// Customer Management Pages
const CustomerListPage = lazy(() => import('../pages/customers/CustomerListPage'));
const CustomerFormPage = lazy(() => import('../pages/customers/CustomerFormPage'));
const LoyaltyReportPage = lazy(() => import('../pages/customers/LoyaltyReportPage'));
const LoyaltyDashboardPage = lazy(() => import('../pages/customers/LoyaltyDashboardPage'));
const CrmDashboardPage = lazy(() => import('../pages/customers/CrmDashboardPage'));
const CustomerReportPage = lazy(() => import('../pages/customers/CustomerReportPage'));
const CustomerCarePage = lazy(() => import('../pages/customers/CustomerCarePage'));
const SegmentsPage = lazy(() => import('../pages/customers/SegmentsPage'));
const CampaignsPage = lazy(() => import('../pages/customers/CampaignsPage'));
const CampaignPage = lazy(() => import('../pages/customers/CampaignPage'));
import AdminOnly from '../components/common/AdminOnly';
import OutletScopeGate from '../components/common/OutletScopeGate';

// Team Management Pages
const TeamDetailPage = lazy(() => import('../pages/teams/TeamDetailPage'));


// Event Settings Page
const EventSettingsPage = lazy(() => import('../pages/events/EventSettingsPage'));

// Check-in Lists Pages
const CheckinListListPage = lazy(() => import('../pages/checkinlists/CheckinListListPage'));
const CheckinListFormPage = lazy(() => import('../pages/checkinlists/CheckinListFormPage'));
const CheckinListDetailPage = lazy(() => import('../pages/checkinlists/CheckinListDetailPage'));

// Question Pages
const QuestionListPage = lazy(() => import('../pages/questions/QuestionListPage'));
const QuestionFormPage = lazy(() => import('../pages/questions/QuestionFormPage'));

// Third-Party Integration Pages
const SupplierListPage = lazy(() => import('../pages/thirdparty/suppliers/SupplierListPage'));
const SupplierFormPage = lazy(() => import('../pages/thirdparty/suppliers/SupplierFormPage'));
const SupplierBulkImportPage = lazy(() => import('../pages/thirdparty/suppliers/SupplierBulkImportPage'));
const ProductListPage = lazy(() => import('../pages/thirdparty/products/ProductListPage'));
const ProductFormPage = lazy(() => import('../pages/thirdparty/products/ProductFormPage'));
const ProductBulkImportPage = lazy(() => import('../pages/thirdparty/products/ProductBulkImportPage'));
const InventoryListPage = lazy(() => import('../pages/thirdparty/inventory/InventoryListPage'));
const InventoryImportPage = lazy(() => import('../pages/thirdparty/inventory/InventoryImportPage'));
const InventoryBulkImportPage = lazy(() => import('../pages/thirdparty/inventory/InventoryBulkImportPage'));
const FulfillmentListPage = lazy(() => import('../pages/thirdparty/fulfillments/FulfillmentListPage'));
const FulfillmentDetailPage = lazy(() => import('../pages/thirdparty/fulfillments/FulfillmentDetailPage'));
const SettlementListPage = lazy(() => import('../pages/thirdparty/settlements/SettlementListPage'));
const SettlementDetailPage = lazy(() => import('../pages/thirdparty/settlements/SettlementDetailPage'));
const ApiCallLogListPage = lazy(() => import('../pages/thirdparty/apicalllogs/ApiCallLogListPage'));
const ApiCallLogDetailPage = lazy(() => import('../pages/thirdparty/apicalllogs/ApiCallLogDetailPage'));

// Event-Scoped Third-Party Pages
const EventProductsPage = lazy(() => import('../pages/events/thirdparty/EventProductsPage'));
const EventInventoryPage = lazy(() => import('../pages/events/thirdparty/EventInventoryPage'));

// 404 and Error Pages
const NotFoundPage = lazy(() => import('../pages/error/NotFoundPage'));

// Content loading fallback
const ContentLoading: React.FC = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
    padding: '20px'
  }}>
    <Spin size="default" tip="Loading content..." spinning={true}>
      <div style={{ minHeight: '100px' }} />
    </Spin>
  </div>
);

interface ProtectedRouteProps {
  children: React.ReactNode;
  permissions?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, permissions }) => {
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  const tenant = useTenant();

  // Show loading while auth is initializing
  if (authLoading) {
    return <ContentLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // For org users, wait for tenant context to be available
  // Don't redirect if tenant is still loading (avoids race conditions on page refresh)
  if (user?.user_type === 'org_user' && !tenant) {
    // Check if we're in a tenant-specific route that requires context
    const pathSegments = window.location.pathname.split('/');
    const isOrgRoute = pathSegments[1] === 'organizations' && pathSegments[2];

    if (isOrgRoute) {
      // We're in an org route but tenant context isn't ready yet
      // Show loading instead of redirecting (prevents infinite loops on page refresh)
      return <ContentLoading />;
    } else {
      // We're not in an org route, redirect to user's organization
      const orgSlug = user?.organization_slug || 'demo-org';
      return <Navigate to={`/organizations/${orgSlug}`} replace />;
    }
  }

  const content = permissions ? (
    <PermissionGuard permissions={permissions}>
      {children}
    </PermissionGuard>
  ) : children;

  // Platform admin đang xem một tổ chức (/organizations/:slug/*): dùng ngữ cảnh org thật để dựng menu + module (ticket 113).
  if (user?.user_type === 'platform_user' && tenant && tenant.organization_slug && tenant.organization_slug !== 'platform') {
    return (
      <AppLayout tenant={tenant} user={user}>
        <Suspense fallback={<ContentLoading />}>
          {content}
        </Suspense>
      </AppLayout>
    );
  }

  // Platform users accessing /admin don't need tenant context for layout
  if (user?.user_type === 'platform_user') {
    // Create synthetic tenant context for platform admin layout
    const platformTenant = {
      organization_slug: 'platform',
      organization: {
        id: 'platform',
        slug: 'platform',
        name: 'Mantik Platform',
        email: 'admin@mantik.com',
        enabled: true,
        created_at: new Date().toISOString(),
        subscription_tier: 'enterprise' as const,
        features: {
          events_enabled: true,
          orders_enabled: true,
          payments_enabled: true,
          gift_cards_enabled: true,
          vouchers_enabled: true,
          dynamic_pricing_enabled: true,
          analytics_dashboard_enabled: true,
          webhooks_enabled: true,
          api_access_enabled: true,
          white_label_enabled: true,
          custom_domain_enabled: true,
          advanced_analytics_enabled: true,
          priority_support_enabled: true,
        },
        branding: {
          primary_color: '#001529',
          secondary_color: '#1890ff',
        },
        settings: {
          timezone: 'UTC',
          currency: 'USD',
          date_format: 'YYYY-MM-DD',
          payment_methods: ['card' as const, 'bank_transfer' as const],
          tax_settings: {
            enabled: false,
            default_rate: 0,
            tax_included_in_prices: false,
            tax_calculation_method: 'item_level' as const,
          },
          notification_preferences: {
            email_notifications: true,
            sms_notifications: false,
            webhook_notifications: true,
          },
        },
      },
      features: {
        events_enabled: true,
        orders_enabled: true,
        payments_enabled: true,
        gift_cards_enabled: true,
        vouchers_enabled: true,
        dynamic_pricing_enabled: true,
        analytics_dashboard_enabled: true,
        webhooks_enabled: true,
        api_access_enabled: true,
        white_label_enabled: true,
        custom_domain_enabled: true,
        advanced_analytics_enabled: true,
        priority_support_enabled: true,
      },
      branding: {
        primary_color: '#001529',
        secondary_color: '#1890ff',
      },
      permissions: ['admin:all', 'platform:access'],
    };

    return (
      <AppLayout tenant={platformTenant} user={user}>
        <Suspense fallback={<ContentLoading />}>
          {content}
        </Suspense>
      </AppLayout>
    );
  }

  // For org users with tenant context available
  if (user?.user_type === 'org_user' && tenant) {
    return (
      <AppLayout tenant={tenant} user={user}>
        <Suspense fallback={<ContentLoading />}>
          {content}
        </Suspense>
      </AppLayout>
    );
  }

  // Fallback - should rarely reach here due to improved logic above
  return <ContentLoading />;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();

  // Show loading while auth is determining state
  if (authLoading) {
    return <ContentLoading />;
  }

  if (isAuthenticated) {
    // Redirect based on user type
    if (user?.user_type === 'platform_user') {
      return <Navigate to="/admin" replace />;
    } else {
      // For org users, redirect to their organization directly
      // In backoffice, users typically belong to only one organization
      const orgSlug = user?.organization_slug || 'demo-org'; // fallback for development
      return <Navigate to={`/organizations/${orgSlug}`} replace />;
    }
  }

  return <>{children}</>;
};

const RootRedirect: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();

  // Show loading while auth is determining state
  if (authLoading) {
    return <ContentLoading />;
  }

  if (user?.user_type === 'platform_user') {
    return <Navigate to="/admin" replace />;
  } else {
    // For org users, redirect to their organization directly
    const orgSlug = user?.organization_slug || 'demo-org'; // fallback for development
    return <Navigate to={`/organizations/${orgSlug}`} replace />;
  }
};

export const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Platform Admin Routes */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute permissions={['platform:access']}>
            <Routes>
              <Route index element={<DashboardPage />} />
              <Route path="system/status" element={<SystemStatusPage />} />
              <Route path="users" element={<UserManagementPage />} />
              <Route path="organizations" element={<OrganizationManagementPage />} />
              <Route path="organizations/import" element={<ImportOrganizationPage />} />
              <Route path="audit-logs" element={<AuditLogsPage />} />
            </Routes>
          </ProtectedRoute>
        }
      />

      {/* Organization Routes */}
      <Route
        path="/organizations/:orgSlug/*"
        element={
          <ProtectedRoute>
            <OutletScopeGate>
            <Routes>
              <Route index element={<OrganizationDashboardPage />} />
              <Route path="users" element={<OrganizationUsersPage />} />
              <Route path="settings" element={<OrganizationSettingsPage />} />
              <Route path="seating-plans" element={<SeatingPlansPage />} />

              {/* Order groups (multi-event orders) — organization level */}
              <Route path="order-groups">
                <Route index element={<OrderGroupListPage />} />
                <Route path=":groupCode" element={<OrderGroupDetailsPage />} />
              </Route>

              {/* Team Management */}
              <Route path="teams/:teamId" element={<TeamDetailPage />} />

              {/* Event Management */}
              <Route path="events">
                <Route index element={<EventListPage />} />
                <Route path="create" element={<EventFormPage mode="create" />} />
                <Route path="import" element={<ImportEventPage />} />
                <Route path=":eventSlug">
                  <Route index element={<EventDashboardPage />} />
                  <Route path="details" element={<EventDetailsPage />} />
                  <Route path="edit" element={<EventFormPage mode="edit" />} />

                  {/* Event Context Routes */}
                  <Route path="items">
                    <Route index element={<ItemListPage />} />
                    <Route path="create" element={<ItemFormPage />} />
                    <Route path=":itemId/edit" element={<ItemFormPage />} />
                  </Route>
                  <Route path="quotas">
                    <Route index element={<QuotaListPage />} />
                    <Route path="create" element={<QuotaFormPage />} />
                    <Route path=":quotaId" element={<QuotaFormPage />} />
                  </Route>
                  <Route path="subevents">
                    <Route index element={<SubEventListPage />} />
                    <Route path="create" element={<SubEventFormPage mode="create" />} />
                    <Route path="price-grid" element={<SubEventPriceGridPage />} />
                    <Route path=":subeventId" element={<SubEventFormPage mode="edit" />} />
                  </Route>
                  <Route path="groups" element={<EventGroupsPage />} />
                  <Route path="categories">
                    <Route index element={<CategoryListPage />} />
                    <Route path="create" element={<CategoryFormPage />} />
                    <Route path=":categoryId" element={<CategoryFormPage />} />
                  </Route>
                  <Route path="orders">
                    <Route index element={<OrderListPage />} />
                    <Route path=":orderCode" element={<OrderDetailsPage />} />
                  </Route>
                  <Route path="vouchers">
                    <Route index element={<VoucherListPage />} />
                    <Route path="create" element={<VoucherCreatePage />} />
                    <Route path=":voucherId/edit" element={<VoucherEditPage />} />
                  </Route>
                  <Route path="checkinlists">
                    <Route index element={<CheckinListListPage />} />
                    <Route path="create" element={<CheckinListFormPage />} />
                    <Route path=":checkinlistId/edit" element={<CheckinListFormPage />} />
                    <Route path=":checkinlistId" element={<CheckinListDetailPage />} />
                  </Route>
                  {/* Check-in devices (G10-06, checkin-app.md §6.1 B4) — chuyển sang event-level (đề xuất
                      người dùng): 1 thiết bị pair theo 1 checkin-list cụ thể, gián tiếp gắn 1 event —
                      trước đây ở organizer-level lẫn lộn thiết bị nhiều event. */}
                  <Route path="checkin-devices" element={<CheckinDeviceListPage />} />
                  <Route path="questions">
                    <Route index element={<QuestionListPage />} />
                    <Route path="create" element={<QuestionFormPage />} />
                    <Route path=":questionId" element={<QuestionFormPage />} />
                  </Route>
                  <Route path="thirdparty">
                    <Route path="products" element={<EventProductsPage />} />
                    <Route path="inventory" element={<EventInventoryPage />} />
                  </Route>
                  <Route path="settings" element={<EventSettingsPage />} />
                </Route>
              </Route>

              {/* Third-Party Integration Routes */}
              <Route path="thirdparty">
                <Route index element={<ProductListPage />} />
                {/* Suppliers */}
                <Route path="suppliers">
                  <Route index element={<SupplierListPage />} />
                  <Route path="create" element={<SupplierFormPage />} />
                  <Route path=":id/edit" element={<SupplierFormPage />} />
                  <Route path="bulk-import" element={<SupplierBulkImportPage />} />
                </Route>
                {/* Products */}
                <Route path="products">
                  <Route index element={<ProductListPage />} />
                  <Route path="create" element={<ProductFormPage />} />
                  <Route path=":id/edit" element={<ProductFormPage />} />
                  <Route path="bulk-import" element={<ProductBulkImportPage />} />
                </Route>
                {/* Inventory */}
                <Route path="inventory">
                  <Route index element={<InventoryListPage />} />
                  <Route path="import" element={<InventoryImportPage />} />
                  <Route path="bulk-import" element={<InventoryBulkImportPage />} />
                </Route>
                {/* Fulfillments */}
                <Route path="fulfillments">
                  <Route index element={<FulfillmentListPage />} />
                  <Route path=":id" element={<FulfillmentDetailPage />} />
                </Route>
                {/* Settlements (task 008) */}
                <Route path="settlements">
                  <Route index element={<SettlementListPage />} />
                  <Route path=":id" element={<SettlementDetailPage />} />
                </Route>
                {/* API call audit log — tra soát request/response gọi ra 3rd-party supplier */}
                <Route path="api-call-logs">
                  <Route index element={<ApiCallLogListPage />} />
                  <Route path=":id" element={<ApiCallLogDetailPage />} />
                </Route>
              </Route>

              {/* Organization-Level Routes */}
              <Route path="customers">
                <Route index element={<CustomerListPage />} />
                <Route path="create" element={<CustomerFormPage />} />
                <Route path="loyalty-report" element={<AdminOnly><LoyaltyReportPage /></AdminOnly>} />
                <Route path="loyalty-dashboard" element={<AdminOnly><LoyaltyDashboardPage /></AdminOnly>} />
                <Route path="crm-dashboard" element={<AdminOnly><CrmDashboardPage /></AdminOnly>} />
                <Route path="crm-customers" element={<AdminOnly><CustomerReportPage /></AdminOnly>} />
                <Route path="care" element={<CustomerCarePage />} />
                <Route path="segments" element={<AdminOnly><SegmentsPage /></AdminOnly>} />
                <Route path="campaigns" element={<AdminOnly><CampaignsPage /></AdminOnly>} />
                <Route path="campaigns/:id" element={<AdminOnly><CampaignPage /></AdminOnly>} />
                <Route path=":customerId/edit" element={<CustomerFormPage />} />
              </Route>
              {/* G9-19 (mantik-cinema-chain.md §5B) — tag SỰ KIỆN/SUẤT CHIẾU, scope theo organizer */}
              <Route path="tags">
                <Route index element={<TagListPage />} />
                <Route path="create" element={<TagFormPage />} />
                <Route path=":tagId" element={<TagFormPage />} />
              </Route>
              {/* G9-32f (f&b.backlog.md GĐ9) — công nợ nội bộ liên đơn vị (Food<->Rạp khi bán combo) */}
              <Route path="unit-ledger">
                <Route path="pair-config" element={<UnitPairConfigPage />} />
                <Route path="settlement-runs">
                  <Route index element={<SettlementRunListPage />} />
                  <Route path=":id" element={<SettlementRunDetailPage />} />
                </Route>
                {/* G9-32i — backfill lịch sử + adjustment thủ công (cùng 1 form, khác entry_type) */}
                <Route path="manual-entry" element={<ManualLedgerEntryPage />} />
                {/* G9-32k6/UL-P10 — entry chưa thuộc settlement run nào ("mồ côi") */}
                <Route path="orphan-entries" element={<OrphanLedgerEntriesPage />} />
                <Route path="owned-events" element={<OwnedEventConfigPage />} />
                {/* G9-32k7(d)/UL-D1 — "hub view" cho cặp relationship_type=internal */}
                <Route path="internal-balances" element={<InternalBalancesPage />} />
              </Route>
              <Route path="payments">
                <Route index element={<PaymentListPage />} />
                <Route path=":paymentId" element={<PaymentDetailsPage />} />
              </Route>
              <Route path="giftcards">
                <Route index element={<GiftCardListPage />} />
                <Route path="create" element={<GiftCardCreatePage />} />
                {/* Đặt trước :giftCardId cho dễ đọc; React Router v6 xếp hạng theo độ cụ thể nên
                    literal luôn thắng param bất kể thứ tự khai báo. */}
                <Route path="refund-tasks" element={<GiftCardRefundTasksPage />} />
                <Route path=":giftCardId" element={<GiftCardDetailsPage />} />
              </Route>
              {/* Phase 1 — voucher cấp organization (đa event), song song route cũ theo event
                  (events/:eventSlug/vouchers ở trên) — KHÔNG xoá route cũ. Cùng component,
                  useParams không có eventSlug ở nhánh này nên tự chuyển sang chế độ org-level. */}
              <Route path="vouchers">
                <Route index element={<VoucherListPage />} />
                <Route path="create" element={<VoucherCreatePage />} />
                <Route path="usage" element={<VoucherUsageReportPage />} />
                <Route path=":voucherId/edit" element={<VoucherEditPage />} />
              </Route>

              {/* GĐ1 — promotion (khuyến mãi tự động, không cần mã), org-level only, ticket-only.
                  Không có route event-scoped (khác voucher) — promotion.proto chỉ có org-scoped CRUD. */}
              <Route path="promotions">
                <Route index element={<PromotionListPage />} />
                <Route path="create" element={<PromotionCreatePage />} />
                <Route path=":promotionId/edit" element={<PromotionEditPage />} />
              </Route>

              {/* CMS Management - Wrapped with CMSLayout for context injection */}
              {/* CMS Management - Using centralized module routes */}
              <Route
                path="cms"
                element={
                  <RiptikCMSProvider>
                    <CMSLayout />
                  </RiptikCMSProvider>
                }
              >
                {cmsRoutes.children?.map((route, idx) => (
                  <Route key={idx} {...route} />
                ))}
              </Route>

              {/* F&B/POS (S-06 scaffold) — chỉ trang chứng minh wiring (outlets); màn nghiệp vụ khác
                  (menu/kho/bếp/báo cáo/HĐĐT) là backlog sau, dùng api.request escape hatch khi cần. */}
              <Route
                path="fnbpos"
                element={
                  <FnbPosProvider>
                    <FnbPosLayout />
                  </FnbPosProvider>
                }
              >
                <Route path="outlets" element={<OutletsPage />} />
                <Route path="orders" element={<OrdersPage />} />
                <Route path="orders/:orderId" element={<OrderDetailPage />} />
                <Route path="staff" element={<StaffPage />} />
                <Route path="masterdata" element={<MasterDataPage />} />
                <Route path="warehouses" element={<WarehousesPage />} />
                <Route path="menu" element={<MenuPage />} />
                <Route path="materials" element={<MaterialsPage />} />
                <Route path="stock" element={<StockOpsPage />} />
                <Route path="purchasing" element={<PurchasingPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="terminals" element={<TerminalShiftPage />} />
                <Route path="reservations" element={<ReservationWaitlistPage />} />
                <Route path="kitchen-config" element={<KitchenConfigPage />} />
                <Route path="floor" element={<FloorPlanPage />} />
                <Route path="selforder" element={<SelfOrderQRPage />} />
                <Route path="takeaway" element={<TakeawayPage />} />
                <Route path="booking-requests" element={<ReservationRequestsPage />} />
                <Route path="tenant-config" element={<TenantConfigPage />} />
                <Route path="bill-designer" element={<BillDesignerPage />} />
                <Route path="report-designer" element={<ReportDesignerPage />} />
                <Route path="payment-config" element={<PaymentHostCredentialPage />} />
                <Route path="payment-reconciliation" element={<PaymentReconciliationPage />} />
              </Route>

              {/* Court (sân pickleball) — mirror fnbpos wiring. Route paths dùng "venues/:supplierId/..."
                  (KHÔNG flatten thành ":supplierId/...") vì các trang trong module tự điều hướng bằng
                  navigate() TƯƠNG ĐỐI dựa trên cấu trúc này (vd CourtsPage `navigate('../..')` để quay
                  lại danh sách venue) — xem doc-block ví dụ trong court-backoffice-module/src/index.ts
                  và src/CourtLayout.tsx. */}
              {/* Giải đấu bản nhẹ (ticket 107) — cấp tổ chức, độc lập với quản lý sân. */}
              <Route
                path="tournaments"
                element={
                  <TournamentProvider>
                    <TournamentLayout />
                  </TournamentProvider>
                }
              >
                <Route index element={<TournamentsPage />} />
                <Route path="refunds" element={<TournamentRefundsPage />} />
                <Route path=":id" element={<TournamentDetailPage />} />
              </Route>
              <Route
                path="court"
                element={
                  <CourtProvider>
                    <CourtLayout />
                  </CourtProvider>
                }
              >
                <Route path="venues" element={<VenuesPage />} />
                <Route path="venues/:supplierId/courts" element={<CourtsPage />} />
                <Route path="venues/:supplierId/courts/:resourceId/pricing" element={<CourtPricingPage />} />
                <Route path="venues/:supplierId/bookings" element={<BookingsPage />} />
                <Route path="venues/:supplierId/settings" element={<VenueSettingsPage />} />
                <Route path="venues/:supplierId/staff" element={<CourtStaffPage />} />
                <Route path="venues/:supplierId/closures" element={<ClosuresPage />} />
                <Route path="venues/:supplierId/deposits" element={<DepositsPage />} />
                <Route path="venues/:supplierId/refunds" element={<RefundsPage />} />
                <Route path="venues/:supplierId/reports" element={<CourtReportsPage />} />
                <Route path="venues/:supplierId/rentals" element={<EventRentalsPage />} />
              </Route>
            </Routes>
            </OutletScopeGate>
          </ProtectedRoute>
        }
      />

      {/* Gift Cards (Global) */}
      <Route
        path="/gift-cards"
        element={
          <ProtectedRoute permissions={['gift_cards:read']}>
            <GiftCardDashboardPage />
          </ProtectedRoute>
        }
      />

      {/* Organization Selector (Legacy - not typically used in backoffice) */}
      <Route
        path="/organizations"
        element={
          <ProtectedRoute>
            <div>
              <h2>Organization Selection</h2>
              <p>In backoffice systems, users typically belong to a single organization.</p>
              <p>You should be automatically redirected to your organization's dashboard.</p>
            </div>
          </ProtectedRoute>
        }
      />

      {/* Root redirect */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <RootRedirect />
          </ProtectedRoute>
        }
      />

      {/* 404 Page */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};